// src/hooks/useActiveWhisper.ts
//
// Subscribes to /users/{uid}/whispers and returns the most recent
// non-dismissed whisper, if any. Whispers are short single-line
// suggestions the server writes when the user lingers near a
// collection-worthy item they haven't found yet — "the Aqua Virgo
// manhole grate is 30 feet to your left if you want to nerd out
// for a second". The chat composer renders the active whisper in
// a small banner above itself.

import { useEffect, useRef, useState } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  doc,
  updateDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { auth, db } from '../services/firebase';

export interface Whisper {
  id: string;
  prose: string;
  /** Structured fields (newer whispers) — when present, the banner
   *  composes the displayed line and the tap-to-ask prompt from
   *  these via t() so the template glue is localized. `prose` and
   *  `action_payload` remain the English fallbacks for old docs. */
  title?: string | null;
  hint?: string | null;
  distance_rounded_m?: number | null;
  /** What action the user can take if they want to engage. Either
   *  'view' (open a detail sheet or map) or 'ask' (push a
   *  follow-up message into chat). */
  action?: 'view' | 'ask' | null;
  /** When action='ask', the prompt to send. When action='view',
   *  the route to navigate to. */
  action_payload?: string | null;
  /** Item / collection this whisper was generated about, for
   *  debugging / impression tracking. */
  collection_id?: string | null;
  item_id?: string | null;
  created_at: Date | null;
  dismissed?: boolean;
  /** Server-side TTL — whispers auto-expire after ~30 min so they
   *  don't accumulate. The hook also filters by this in case the
   *  janitor is behind. */
  expires_at?: Date | null;
}

const tsToDate = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Timestamp) return v.toDate();
  if (typeof v?.toDate === 'function') return v.toDate();
  return null;
};

export function useActiveWhisper(): {
  whisper: Whisper | null;
  dismiss: () => Promise<void>;
} {
  const [whisper, setWhisper] = useState<Whisper | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setWhisper(null);
      return;
    }
    unsubRef.current?.();

    // Only undismissed; sorted by created_at desc; take 5 and pick
    // the freshest non-expired client-side. The Firestore query
    // can't combine inequality on expires_at with sort on
    // created_at without a composite index, so we filter
    // client-side instead.
    const ref = collection(db, 'users', user.uid, 'whispers');
    const q = query(
      ref,
      where('dismissed', '==', false),
      orderBy('created_at', 'desc')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const now = Date.now();
        for (const d of snap.docs) {
          const data = d.data() as any;
          const expires = tsToDate(data.expires_at);
          if (expires && expires.getTime() < now) continue;
          setWhisper({
            id: d.id,
            prose: data.prose || '',
            title: typeof data.title === 'string' ? data.title : null,
            hint: typeof data.hint === 'string' ? data.hint : null,
            distance_rounded_m:
              typeof data.distance_rounded_m === 'number'
                ? data.distance_rounded_m
                : null,
            action: data.action || null,
            action_payload: data.action_payload || null,
            collection_id: data.collection_id || null,
            item_id: data.item_id || null,
            created_at: tsToDate(data.created_at),
            dismissed: !!data.dismissed,
            expires_at: expires,
          });
          return;
        }
        setWhisper(null);
      },
      (err) => {
        console.warn('whispers listener err:', err);
      }
    );
    unsubRef.current = unsub;
    return () => unsubRef.current?.();
  }, []);

  const dismiss = async () => {
    const user = auth.currentUser;
    if (!user || !whisper) return;
    try {
      await updateDoc(
        doc(db, 'users', user.uid, 'whispers', whisper.id),
        { dismissed: true }
      );
    } catch (err) {
      console.warn('whisper dismiss failed:', err);
    }
  };

  return { whisper, dismiss };
}
